import { useEffect, useRef } from 'react';

const specs = [
  {
    icon: 'ri-box-3-line',
    label: '본체 재질',
    value: '엔지니어링 플라스틱',
    sub: '사출 성형 · 고강도 내구 구조',
    accent: false,
  },
  {
    icon: 'ri-drop-fill',
    label: '패킹 재질',
    value: '고성능 실리콘',
    sub: '반영구 일체형 · 분리 불가 설계',
    accent: false,
  },
  {
    icon: 'ri-palette-line',
    label: '색상',
    value: '다크 차콜 그레이',
    sub: '현장 친화적 중립 컬러',
    accent: false,
  },
  {
    icon: 'ri-layout-grid-2-line',
    label: '구조',
    value: '플립탑 힌지 커버',
    sub: '이중 압착 실링 시스템',
    accent: false,
  },
  {
    icon: 'ri-archive-stack-line',
    label: '포장 단위',
    value: '1,000개 / 박스',
    sub: '현장 수급 효율 최적화',
    accent: false,
  },
  {
    icon: 'ri-time-line',
    label: '내구성',
    value: '장기간 방수 보증',
    sub: '장기 품질 보장 설계',
    accent: false,
  },
  {
    icon: 'ri-flashlight-line',
    label: '시공 방식',
    value: '원스텝 (One-Step)',
    sub: '별도 실리콘 도포작업 불필요',
    accent: true,
  },
  {
    icon: 'ri-shield-star-line',
    label: '특허',
    value: '특허 등록 완료',
    sub: '2026년 7월 30일 (특허 완료)',
    accent: true,
  },
];

const SpecsSection = () => {
  const titleRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
          }
        });
      },
      { threshold: 0.1 }
    );
    [titleRef, gridRef].forEach((r) => r.current && observer.observe(r.current));
    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="specs" className="py-24 bg-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">

        {/* 타이틀 */}
        <div
          ref={titleRef}
          className="text-center mb-14 opacity-0 translate-y-6 transition-all duration-700"
        >
          <span className="text-[#FF6B35] text-sm font-semibold tracking-widest uppercase">
            Specifications
          </span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#1B2A4A] font-display">
            제품 스펙
          </h2>
          <p className="mt-4 text-gray-500 text-base max-w-xl mx-auto">
            엄격한 품질 기준으로 설계된 Smart Cap의 주요 사양
          </p>
          <div className="mt-4 w-12 h-1 bg-[#FF6B35] mx-auto rounded-full" />
        </div>

        {/* 카드 그리드 */}
        <div
          ref={gridRef}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {specs.map((spec, i) => (
            <div
              key={i}
              ref={(el) => { cardRefs.current[i] = el; }}
              className={`group relative rounded-2xl border p-5 flex flex-col gap-3
                opacity-0 translate-y-6 transition-all duration-500 cursor-default
                ${spec.accent
                  ? 'bg-[#1B2A4A] border-[#FF6B35]/40 hover:border-[#FF6B35]'
                  : 'bg-white border-gray-100 hover:border-[#1B2A4A]/20 hover:bg-[#F8F9FC]'
                }`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              {/* 아이콘 */}
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl transition-colors
                ${spec.accent
                  ? 'bg-[#FF6B35]/20 group-hover:bg-[#FF6B35]/30'
                  : 'bg-[#1B2A4A]/5 group-hover:bg-[#FF6B35]/10'
                }`}>
                <i className={`${spec.icon} text-lg transition-colors
                  ${spec.accent ? 'text-[#FF6B35]' : 'text-[#1B2A4A] group-hover:text-[#FF6B35]'}`} />
              </div>

              {/* 텍스트 */}
              <div>
                <p className={`text-xs mb-1 font-medium tracking-wide
                  ${spec.accent ? 'text-gray-400' : 'text-gray-400'}`}>
                  {spec.label}
                </p>
                <p className={`font-bold text-sm leading-snug
                  ${spec.accent ? 'text-white' : 'text-[#1B2A4A]'}`}>
                  {spec.value}
                </p>
                <p className={`text-xs mt-1.5 leading-snug
                  ${spec.accent ? 'text-gray-400' : 'text-gray-400'}`}>
                  {spec.sub}
                </p>
              </div>

              {/* accent 카드 오른쪽 상단 뱃지 */}
              {spec.accent && (
                <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-[#FF6B35]" />
              )}
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default SpecsSection;
